import {
  GridColDef,
  GridColumnGroupingModel,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import {Pill} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {CSSProperties, FC, useMemo} from 'react';

import {CellValueWithPopupOver} from '../../../../Browse2/CellValueWithPopupOver';
import {isCallChat, normalizeChatTraceCall} from '../../ChatView/hooks';
import {MessageList} from '../../ChatView/MessageList';
import {MessagePanel} from '../../ChatView/MessagePanel';
import {
  ChatCompletion,
  ChatRequest,
  Message,
  MessagePart,
} from '../../ChatView/types';
import {FlattenedCallData} from '../CallsTable';

const LLM_COMPLETIONS_TOOLTIP =
  'Inferred from your data, these columns show the last turn of the LLM completion and the response.';

// Shared styles for cell content
const CELL_CONTAINER_STYLE: CSSProperties = {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

// Base column config for LLM completion columns
const BASE_COLUMN_CONFIG = {
  sortable: false,
  filterable: false,
  resizable: true,
  display: 'flex' as const,
  width: 150,
};

export const deriveLLMChatCompletionColumnsAndGroups = (
  tableData: FlattenedCallData[]
): {
  derivedColumns: Array<GridColDef<FlattenedCallData>>;
  derivedGroups: GridColumnGroupingModel;
} => {
  let hasLLMChatCalls = false;
  try {
    hasLLMChatCalls = tableData.some(
      row => row.__traceCall__ && isCallChat(row.__traceCall__)
    );
  } catch {
    return {derivedColumns: [], derivedGroups: []};
  }

  if (!hasLLMChatCalls) {
    return {derivedColumns: [], derivedGroups: []};
  }

  const derivedColumns: Array<GridColDef<FlattenedCallData>> = [
    {
      ...BASE_COLUMN_CONFIG,
      field: 'LLM Completion.last_input_message',
      headerName: 'Last Turn',
      renderCell: (cellParams: GridRenderCellParams) => {
        let inputMessages: Message[] = [];
        try {
          const {inputs} = normalizeChatTraceCall(cellParams.row.__traceCall__);
          inputMessages = (inputs as ChatRequest)?.messages ?? [];
        } catch {
          return null;
        }

        if (inputMessages.length === 0) {
          return null;
        }

        const lastMessageChunk =
          extractLastMessageChunkFromMessages(inputMessages);

        if (lastMessageChunk?.type === 'single') {
          return <CellValueForLLMMessage message={lastMessageChunk.message} />;
        }

        if (lastMessageChunk?.type === 'tool_response') {
          return (
            <CellValueForLLMMessages
              toolResultMessages={lastMessageChunk.toolResultMessages}
              invokingToolCallMessages={
                lastMessageChunk.invokingToolCallMessages
              }
            />
          );
        }

        return null;
      },
    },
    {
      ...BASE_COLUMN_CONFIG,
      field: 'LLM Completion.output_message',
      headerName: 'Response',
      renderCell: (cellParams: GridRenderCellParams) => {
        let outputMessage: Message | null = null;
        try {
          const {output} = normalizeChatTraceCall(cellParams.row.__traceCall__);
          outputMessage =
            (output as ChatCompletion)?.choices[0].message ?? null;
        } catch {
          return null;
        }
        return outputMessage ? (
          <CellValueForLLMMessage message={outputMessage} />
        ) : null;
      },
    },
  ];

  const derivedGroups: GridColumnGroupingModel = [
    {
      groupId: 'LLM Completions',
      headerName: 'LLM Completions',
      renderHeaderGroup: params => {
        return (
          <Tooltip
            content={LLM_COMPLETIONS_TOOLTIP}
            trigger={
              <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                <Icon
                  name="lightboard"
                  width={12}
                  height={12}
                  style={{color: 'rgba(0, 0, 0, 0.54)'}}
                />{' '}
                LLM Completions
              </div>
            }
          />
        );
      },
      children: [
        {field: 'LLM Completion.last_input_message'},
        {field: 'LLM Completion.output_message'},
      ],
    },
  ];

  return {derivedColumns, derivedGroups};
};

// Extract text content from a message part
const extractTextFromMessagePart = (
  part: MessagePart
): string | React.ReactNode | null => {
  if (typeof part === 'string') {
    return part;
  }

  if (part.type === 'text' && 'text' in part && typeof part.text === 'string') {
    return part.text;
  }

  if ('content' in part && typeof part.content === 'string') {
    return part.content;
  }

  if (part.type === 'image_url') {
    return <Pill label="Image" color="blue" icon="panel-images" />;
  }

  return null;
};

// Extract text content from a message
const extractTextFromMessage = (
  message: Message
): string | React.ReactNode | null => {
  if (!message.content) {
    return null;
  }

  if (typeof message.content === 'string') {
    return message.content;
  }

  if (Array.isArray(message.content)) {
    return (
      <>{message.content.map(extractTextFromMessagePart).filter(Boolean)}</>
    );
  }

  return null;
};

// Render tool call pills
const renderToolCallPills = (message: Message) => {
  if (!message.tool_calls?.length) {
    return null;
  }

  return (
    <>
      {message.tool_calls.map(toolCall => (
        <Pill
          key={toolCall.id}
          label={toolCall.function.name}
          color="blue"
          icon="code-alt"
        />
      ))}
    </>
  );
};

// Render a tool response pill
const renderToolResponsePill = (message: Message) => (
  <Pill label={message.name ?? 'Tool'} color="gold" />
);

// Get icon for message role
const getMessageIcon = (message: Message): IconName => {
  switch (message.role) {
    case 'user':
      return 'user-profile-personal';
    case 'system':
    case 'developer':
      return 'system-chip';
    case 'assistant':
      return message.tool_calls?.length
        ? 'automation-robot-arm'
        : 'robot-service-member';
    case 'tool':
      return 'code-alt';
    default:
      return 'user-profile-personal';
  }
};

const CellValueForLLMMessage: FC<{message: Message}> = ({message}) => {
  const cellValue = (
    <div style={CELL_CONTAINER_STYLE}>
      <Icon
        style={{width: '16px', height: '16px'}}
        role="presentation"
        name={getMessageIcon(message)}
      />
      <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
        {renderToolCallPills(message)}
      </div>
      <span>{extractTextFromMessage(message)}</span>
    </div>
  );

  const popoverBody = (
    <Tailwind>
      <MessagePanel
        index={0}
        message={message}
        isNested
        isLast
        startShowingMore
      />
    </Tailwind>
  );

  return (
    <CellValueWithPopupOver
      collapsed={cellValue}
      popoverBody={popoverBody}
      tooltip={popoverBody}
    />
  );
};

type LastMessageChunkSingle = {
  type: 'single';
  message: Message;
};

type LastMessageChunkToolResponse = {
  type: 'tool_response';
  toolResultMessages: Message[];
  invokingToolCallMessages: Message[];
};

/**
 * Extracts the last meaningful chunk from a message sequence.
 *
 * For simple cases, returns the last message.
 * For tool usage patterns, groups trailing tool responses with their invoking messages.
 * This handles the pattern: assistant (with tool_calls) -> tool -> tool -> ...
 */
const extractLastMessageChunkFromMessages = (
  messages: Message[]
): LastMessageChunkSingle | LastMessageChunkToolResponse | null => {
  if (messages.length === 0) {
    return null;
  }

  const lastMessage = messages[messages.length - 1];

  // If last message is not a tool response, return it as a single message
  if (lastMessage.role !== 'tool') {
    return {type: 'single', message: lastMessage};
  }

  // Collect all trailing tool result messages
  const toolResultMessages: Message[] = [];
  const neededToolIds = new Set<string>();

  for (
    let i = messages.length - 1;
    i >= 0 && messages[i].role === 'tool';
    i--
  ) {
    const message = messages[i];
    toolResultMessages.unshift(message);
    if (message.tool_call_id) {
      neededToolIds.add(message.tool_call_id);
    }
  }

  // Find messages that invoked these tools (working backwards from the tool messages)
  const invokingToolCallMessages: Message[] = [];

  for (let i = messages.length - 1; i >= 0 && neededToolIds.size > 0; i--) {
    const message = messages[i];
    const matchingToolCalls =
      message.tool_calls?.filter(tc => neededToolIds.has(tc.id)) ?? [];

    if (matchingToolCalls.length > 0) {
      invokingToolCallMessages.unshift(message);
      matchingToolCalls.forEach(tc => neededToolIds.delete(tc.id));
    }
  }

  return {
    type: 'tool_response',
    toolResultMessages,
    invokingToolCallMessages,
  };
};

const CellValueForLLMMessages: FC<{
  toolResultMessages: Message[];
  invokingToolCallMessages: Message[];
}> = ({toolResultMessages, invokingToolCallMessages}) => {
  const cellValue = (
    <div style={CELL_CONTAINER_STYLE}>
      <Icon
        style={{width: '16px', height: '16px'}}
        role="presentation"
        name="job-program-code"
      />
      {toolResultMessages.map((message, idx) => (
        <React.Fragment key={idx}>
          {renderToolResponsePill(message)}
        </React.Fragment>
      ))}
    </div>
  );

  // Combine invoking messages (without content) and tool result messages for popover
  const messages = useMemo(
    () => [
      ...invokingToolCallMessages.map(msg => ({
        ...msg,
        content: undefined,
        text: undefined,
      })),
      ...toolResultMessages,
    ],
    [invokingToolCallMessages, toolResultMessages]
  );

  const popoverBody = (
    <Tailwind>
      <MessageList messages={messages} />
    </Tailwind>
  );

  return (
    <CellValueWithPopupOver
      collapsed={cellValue}
      popoverBody={popoverBody}
      tooltip={popoverBody}
    />
  );
};
